import type { ManagedDeviceDetail, RemoteLaunchResponse } from "@noverik/contracts";
import { ArrowLeft, Cpu, ExternalLink, HardDrive, MemoryStick, Monitor, Tag, Database } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiRequest, ApiRequestError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";

function formatBytes(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return "Não coletado";
  return (value / 1073741824).toLocaleString("pt-BR", { maximumFractionDigits: 1 }) + " GiB";
}

export function DeviceDetailPage() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const { session } = useAuth();
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [manualUrl, setManualUrl] = useState<string | null>(null);
  const [device, setDevice] = useState<ManagedDeviceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    if (!deviceId) {
      setError("Equipamento não informado.");
      setLoading(false);
      return;
    }
    apiRequest<ManagedDeviceDetail>("/agent/devices/" + encodeURIComponent(deviceId))
      .then(data => { if (active) setDevice(data); })
      .catch(reason => {
        if (active) setError(reason instanceof ApiRequestError ? reason.message : "Falha ao consultar o equipamento.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [deviceId]);

  const launchRemote = async () => {
    if (!device?.canRemoteLaunch || !deviceId || launching) return;
    setLaunching(true);
    setLaunchError(null);
    setManualUrl(null);
    // Reserve the new tab during the user's click. Waiting for fetch() before
    // opening a tab is blocked by popup blockers in many browsers.
    const popup = window.open("about:blank", "_blank");
    if (popup) {
      popup.opener = null;
      popup.document.title = "Aguardando autorização — Noverik Remote";
      popup.document.body.textContent = "Verificando permissão de acesso remoto...";
    }
    try {
      const result = await apiRequest<RemoteLaunchResponse>(
        "/agent/devices/" + encodeURIComponent(deviceId) + "/remote-launch",
        { method: "POST", csrfToken: session?.csrfToken, body: {} },
      );
      // Defense in depth: never redirect an operator to a remote origin
      // chosen by a compromised API, and never accept SSO/auth tokens here.
      const url = new URL(result.url);
      if (url.origin !== "https://remoto.noverik.com.br"
          || url.pathname !== "/"
          || url.searchParams.get("viewmode") !== "11"
          || !url.searchParams.get("node")
          || !result.requiresMeshCentralLogin) {
        throw new Error("Destino remoto inválido.");
      }
      if (popup && !popup.closed) {
        popup.location.replace(url.toString());
      } else {
        // If the browser blocked the tab, a separate user click can open it.
        setManualUrl(url.toString());
      }
    } catch (reason) {
      if (popup && !popup.closed) popup.close();
      setLaunchError(reason instanceof ApiRequestError || reason instanceof Error
        ? reason.message : "Não foi possível preparar o acesso remoto.");
    } finally {
      setLaunching(false);
    }
  };

  const inv = device?.latestInventory;
  return (
    <div className="page">
      <Link className="remote-back" to="/desk/equipamentos"><ArrowLeft size={16} /> Voltar aos equipamentos</Link>
      {loading && <div className="data-message">Carregando inventário...</div>}
      {error && <div className="data-message error">{error}</div>}
      {!loading && device && (
        <>
          <div className="page-heading">
            <div>
              <span className="eyebrow">NOVERIK REMOTE · {device.organization.name}</span>
              <h1>{device.hostname}</h1>
              <p>{device.operatingSystem ?? "Sistema operacional não informado"} · {device.status === "online" ? "Online" : device.status === "offline" ? "Offline" : "Sem informação"}</p>
            </div>
            {device.canRemoteLaunch && (
              <button type="button" className="button primary" onClick={() => void launchRemote()} disabled={launching}>
                <ExternalLink size={17} /> {launching ? "Autorizando..." : "Iniciar acesso remoto"}
              </button>
            )}
          </div>
          {launchError && <div className="data-message error" role="alert">{launchError}</div>}
          {manualUrl && (
            <div className="remote-notice">
              Seu navegador bloqueou a nova aba.{" "}
              <a href={manualUrl} target="_blank" rel="noopener noreferrer">Abrir MeshCentral agora</a>.
            </div>
          )}
          {device.stale && <div className="remote-notice">A sincronização está desatualizada. Não presuma que o status represente a conexão atual.</div>}
          <div className="remote-inventory-grid">
            <article className="remote-inventory-card"><Monitor size={22} /><span>Cliente</span><strong>{device.organization.name}</strong></article>
            <article className="remote-inventory-card"><Cpu size={22} /><span>Processador</span><strong>{inv?.cpu ?? "Não coletado"}</strong></article>
            <article className="remote-inventory-card"><MemoryStick size={22} /><span>Memória RAM</span><strong>{formatBytes(inv?.memoryBytes)}</strong></article>
            <article className="remote-inventory-card"><HardDrive size={22} /><span>Armazenamento</span><strong>{inv?.disks.length ? inv.disks.map(d => d.name + " · " + formatBytes(d.capacityBytes)).join(", ") : "Não coletado"}</strong></article>
          </div>
          <div className="remote-inventory-panel">
            <h2>Identificação do computador</h2>
            <dl>
              <div><dt>Fabricante</dt><dd>{inv?.manufacturer ?? "Não informado"}</dd></div>
              <div><dt>Modelo</dt><dd>{inv?.model ?? "Não informado"}</dd></div>
              <div><dt>Patrimônio / Asset tag</dt><dd><Tag size={14} className="remote-inline-icon" />{inv?.assetTag ?? "Não informado"}</dd></div>
              <div><dt>Tags no MeshCentral</dt><dd>{device.meshTags?.length
                ? <span className="remote-tag-list">{device.meshTags.map(tag => <span key={tag}>{tag}</span>)}</span>
                : "Sem tags"}</dd></div>
              <div><dt>Placa-mãe</dt><dd>{inv?.motherboard ?? "Não coletado"}</dd></div>
              <div><dt>Fabricante da BIOS</dt><dd>{inv?.biosVendor ?? "Não informado"}</dd></div>
              <div><dt>Versão da BIOS</dt><dd>{inv?.biosVersion ?? "Não coletado"}</dd></div>
              <div><dt>Plataforma</dt><dd>{device.platform}</dd></div>
              <div><dt>Agente MeshCentral</dt><dd>{device.agentVersion ?? "Não informado"}</dd></div>
              <div><dt>Última coleta do hardware</dt><dd>{inv?.collectedAt ? new Date(inv.collectedAt).toLocaleString("pt-BR") : "Não coletado"}</dd></div>
              <div><dt>Última sincronização</dt><dd>{new Date(device.collectedAt).toLocaleString("pt-BR")}</dd></div>
            </dl>
          </div>
          <div className="remote-inventory-panel remote-volumes-panel">
            <h2><Database size={19} /> Volumes e partições</h2>
            <p className="remote-panel-description">Unidades lógicas informadas pelo agente. Espaço livre e utilização não são calculados a partir da capacidade dos discos físicos.</p>
            {inv?.volumes?.length ? (
              <div className="remote-volumes-scroll">
                <table className="remote-volumes-table">
                  <thead><tr><th>Unidade</th><th>Nome</th><th>Sistema de arquivos</th><th>Capacidade</th><th>Livre</th><th>Utilizado</th></tr></thead>
                  <tbody>{inv.volumes.map((vol, i) => {
                    const percent = vol.capacityBytes != null && vol.capacityBytes > 0 && vol.freeBytes != null
                      ? Math.round((1 - vol.freeBytes / vol.capacityBytes) * 100) : null;
                    return (
                      <tr key={vol.mount + i}>
                        <td><strong>{vol.mount}</strong></td>
                        <td>{vol.label ?? "Sem rótulo"}</td>
                        <td>{vol.fileSystem ?? "Não informado"}</td>
                        <td>{formatBytes(vol.capacityBytes)}</td>
                        <td>{formatBytes(vol.freeBytes)}</td>
                        <td>{percent != null ? <div className="remote-volume-meter">
                          <div className="remote-volume-track"><div style={{ width: Math.max(0, Math.min(100, percent)) + "%" }} /></div>
                          <span>{percent}%</span>
                        </div> : "Não coletado"}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            ) : <div className="remote-empty">Volumes ainda não disponíveis no inventário do MeshCentral.</div>}
          </div>
          <div className="remote-inventory-panel">
            <h2><HardDrive size={19} /> Discos físicos</h2>
            {inv?.disks?.length ? <div className="remote-disk-list">
              {inv.disks.map((disk, i) => <div key={disk.name + i}>
                <strong>{disk.name}</strong>
                <span>{formatBytes(disk.capacityBytes)}</span>
              </div>)}
            </div> : <div className="remote-empty">Discos físicos não coletados.</div>}
          </div>
          <div className="remote-notice">
            {device.canRemoteLaunch
              ? "O acesso será aberto em uma nova aba. Entre com a sua própria conta do MeshCentral e autenticação de dois fatores. O Desk registra a abertura do link, não a conexão efetiva."
              : device.remoteAccessIntegrated
                ? "Acesso remoto indisponível para esta conta ou equipamento. Solicite permissão para a organização e utilize uma conta individual do MeshCentral."
                : "Acesso remoto a partir do Desk ainda não foi ativado nesta instalação. Utilize o MeshCentral com sua própria conta."}
          </div>
        </>
      )}
    </div>
  );
}
